import React, { useState } from "react";
import { Calendar, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { CONTACT } from "../mock";

const INITIAL = {
  name: "", phone: "", dob: "", tob: "", pob: "", service: "", query: "",
};

const ConsultForm = () => {
  const [data, setData] = useState(INITIAL);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.phone || !data.dob || !data.service) return;
    setStatus("loading");
    setErrorMsg("");

    // Build payload for FormSubmit.co (free, no-signup email forwarding service)
    // Docs: https://formsubmit.co
    const payload = new FormData();
    payload.append("name", data.name);
    payload.append("phone", data.phone);
    payload.append("date_of_birth", data.dob);
    payload.append("time_of_birth", data.tob || "Not provided");
    payload.append("place_of_birth", data.pob || "Not provided");
    payload.append("service_requested", data.service);
    payload.append("query", data.query || "—");
    payload.append("submitted_at", new Date().toLocaleString("en-IN"));
    // FormSubmit configuration (hidden fields starting with underscore)
    payload.append("_subject", `New Consultation Request — ${data.name}`);
    payload.append("_template", "table");
    payload.append("_captcha", "false");

    try {
      const res = await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(CONTACT.formEmail)}`, {
        method: "POST",
        headers: { "Accept": "application/json" },
        body: payload,
      });
      // Treat any 2xx response as success. FormSubmit returns 200 on the very first
      // submission (which triggers the activation email) — we want to show the user a
      // success message either way; future submissions will go through automatically
      // once the recipient confirms via the activation email.
      if (res.ok) {
        setStatus("success");
        setData(INITIAL);
        setTimeout(() => setStatus("idle"), 8000);
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        "Could not send your request. Please call " + CONTACT.callDisplay +
        " or WhatsApp " + CONTACT.whatsappDisplay + " directly."
      );
    }
  };

  return (
    <section id="consult" className="consult">
      <div className="container">
        <div className="consult-inner">
          <span className="section-label center">Free First Call</span>
          <h2 className="section-title">Book Your <em>Free</em><br/>Consultation Today</h2>
          <p className="section-desc" style={{ margin: "14px auto 0" }}>
            Take the first step toward clarity. Your 1st call with Guruji is completely free.
          </p>

          <form className="consult-form" onSubmit={onSubmit}>
            {status === "success" && (
              <div className="form-alert success">
                <CheckCircle2 size={18} /> Thank you! Guruji's team will reach out within 24 hours.
              </div>
            )}
            {status === "error" && (
              <div className="form-alert error">
                <AlertCircle size={18} /> {errorMsg}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Your Name *</label>
                <input value={data.name} onChange={onChange("name")} placeholder="Full name" required />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input type="tel" value={data.phone} onChange={onChange("phone")} placeholder="+91 XXXXX XXXXX" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth *</label>
                <input type="date" value={data.dob} onChange={onChange("dob")} required />
              </div>
              <div className="form-group">
                <label>Time of Birth</label>
                <input type="time" value={data.tob} onChange={onChange("tob")} />
              </div>
            </div>
            <div className="form-group">
              <label>Place of Birth</label>
              <input value={data.pob} onChange={onChange("pob")} placeholder="City, State, Country" />
            </div>
            <div className="form-group">
              <label>Select Service *</label>
              <select value={data.service} onChange={onChange("service")} required>
                <option value="">Choose a Service</option>
                <option>Palmistry Reading</option>
                <option>Horoscope / Kundali Reading</option>
                <option>Love & Marriage Problems</option>
                <option>Career & Finance Guidance</option>
                <option>Family Problems</option>
                <option>Black Magic Removal</option>
                <option>Vastu Consultation</option>
                <option>Ganesh Homa</option>
                <option>Navagraha Pooja</option>
                <option>KaalSarp Dosha Pooja</option>
                <option>Negative Energy Removal</option>
                <option>Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Your Query</label>
              <textarea value={data.query} onChange={onChange("query")} placeholder="Briefly describe what guidance you are seeking..." />
            </div>
            <button type="submit" className="btn btn-primary form-submit" disabled={status === "loading"}>
              {status === "loading" ? <><Loader2 size={16} className="spin" /> Submitting...</> : <><Calendar size={16} /> Submit & Get Free Consultation</>}
            </button>
            <p className="form-note">Your information is 100% confidential. We respond within 24 hours.</p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ConsultForm;
